/**
 * Browser Position Tracker
 * Replicates positions-live.js functionality for browser environment
 * 
 * Key differences from Electron:
 * - No child process (runs in main thread)
 * - Uses WebSocket subscriptions + 30-second polling (same as Electron)
 * - Emits CustomEvent instead of stdout/IPC
 * - Singleton pattern to ensure only one instance runs
 */

import { WebSocketTransport, SubscriptionClient, HttpTransport, InfoClient } from '@nktkas/hyperliquid';
import { DualInfoClient } from '../../utils/dual-info-client.js';

export class BrowserPositionTracker {
    constructor() {
        this.isRunning = false;
        this.pollInterval = null;
        this.transport = null;
        this.subsClient = null;
        this.infoClient = null;
        this.positions = new Map();
        this.orders = new Map();
        this.accountSummary = {};
        this.publicAddress = null;
        this.isConnected = false;
        
        // Separate storage for entry orders vs TP/SL orders (like Electron)
        this.entryOrders = new Map();
        this.tpslOrders = new Map();
        
        // SINGLE SOURCE OF TRUTH: Track when we last got ANY data update
        // Updated on: polls, fills, cancels - any activity = "Live"
        this.lastDataUpdate = null;
        
        // DEX rotation optimization: Query Main + one rotating HIP-3 DEX per poll
        this.dexRotationIndex = 0;  // Track which HIP-3 DEX to query next
        this.allKnownDexs = null;   // Cache all available DEXs
        this.dexsCacheTime = null;  // When we last fetched DEX list
        
        // WS tracking for Main DEX only (HIP-3 comes from API polls)
        this.wsAccountSummary = null;          // From clearinghouseState WS
        this.lastWsAccountUpdate = 0;          // Timestamp of last WS account update
        this.wsOrdersMap = new Map();          // From openOrders WS (Main DEX only)
        this.lastWsOrdersUpdate = 0;           // Timestamp of last WS orders update
        
        // Spot balance cache (prevents flickering when WS updates arrive before API poll)
        this.cachedUsdhBalance = 0;            // Last known USDH balance from API
        this.usdhFetched = false;              // Track if USDH has been fetched from API at least once
        this.useApiAsSource = false;           // Flag: true = API priority, false = WS priority
        
        // Polling mutex: Prevent race conditions between polls and WebSocket updates
        this.isPolling = false;     // Lock flag to prevent overlapping polls
        this.pollQueue = [];        // Queue for requests made during active poll
        
        // Remote node configuration
        this.remoteNodeUrl = null;
        this.remoteTransport = null;
        this.remoteInfoClient = null;
        this.useRemoteNode = false;
        
        // Order debounce state (2-poll confirmation to prevent flickering)
        this.previousOrderIds = new Set();           // Last broadcast order IDs
        this.pendingAdds = new Map();                // oid → { order, pollCount }
        this.pendingRemoves = new Map();             // oid → pollCount
        this.debounceEnabled = true;                 // Disable in fallback mode (30s too slow)
        this.DEBOUNCE_POLL_COUNT = 2;                // Require 2 polls for confirmation
        
        // Cross-tab coordination
        this.mode = 'inactive'; // 'leader', 'follower', 'inactive'
        this.broadcastChannel = null;
    }

    /**
     * Start position tracking with WebSocket + polling (singleton)
     * @param {string} publicAddress - User's public wallet address
     * @param {string} mode - Operating mode: 'leader' or 'follower'
     */
    async start(publicAddress, mode = 'leader') {
        // Always set publicAddress first (needed for WebSocket subscriptions)
        this.publicAddress = publicAddress;
        this.mode = mode;
        
        // If follower mode, setup BroadcastChannel listener instead of WebSocket
        if (mode === 'follower') {
            return this.startFollowerMode();
        }
        
        if (this.isRunning) {
            // console.log('[POSITION-TRACKER] Already running, checking for data...');
            
            // OPTION 3: Check cache age before using (5-second expiration)
            if (window.lastPositionsData && window.lastPositionsData.accountSummary?.accountValue !== undefined) {
                const cacheAge = Date.now() - (window.lastPositionsData.timestamp || 0);
                const cacheExpired = cacheAge > 5000; // 5 seconds
                
                if (!cacheExpired) {
                    // Cache is fresh - use it immediately
                    this.accountSummary = window.lastPositionsData.accountSummary;
                    this.lastDataUpdate = window.lastPositionsData.timestamp;
                    
                    // Restore positions from cache
                    this.positions.clear();
                    if (window.lastPositionsData.positions && Array.isArray(window.lastPositionsData.positions)) {
                        window.lastPositionsData.positions.forEach(pos => {
                            this.positions.set(pos.coin, pos);
                        });
                    }
                    
                    // Restore orders from cache
                    this.orders.clear();
                    if (window.lastPositionsData.orders && Array.isArray(window.lastPositionsData.orders)) {
                        window.lastPositionsData.orders.forEach(order => {
                            this.orders.set(order.oid, order);
                        });
                    }
                    
                    // Reprocess TP/SL orders
                    this.processTPSLOrders();
                    
                    this.broadcastData();
                    return;
                }
            }
            
            // Cache expired or missing - force fresh poll and WAIT
            await this.pollData();
            return;
        }

        // console.log('[POSITION-TRACKER] Starting WebSocket + polling for:', publicAddress);
        
        // CRITICAL FIX: Clear any stale data from previous sessions
        this.positions.clear();
        this.orders.clear();
        this.entryOrders.clear();
        this.tpslOrders.clear();
        this.accountSummary = {};
        
        // publicAddress already set at top of function
        this.isRunning = true;
        
        // Setup BroadcastChannel for leader mode (to broadcast updates)
        if (!this.broadcastChannel) {
            this.broadcastChannel = new BroadcastChannel('atv4_positions');
            this.broadcastChannel.onmessage = (e) => this.handleBroadcast(e.data);
        }

        try {
            await this.connect();
        } catch (error) {
            // console.error('[POSITION-TRACKER] Failed to start:', error);
            this.isRunning = false;
        }
    }

    /**
     * Connect to Hyperliquid WebSocket and start subscriptions
     * Matches positions-live.js connect() method
     */
    async connect() {
        try {
            // console.log('[POSITION-TRACKER] Connecting to Hyperliquid WebSocket...');
            
            // Create WebSocket transport (same config as positions-live.js)
            this.transport = new WebSocketTransport({
                url: 'wss://api.hyperliquid.xyz/ws',
                timeout: 10000,
                keepAlive: {
                    interval: 30000,
                    timeout: 10000
                },
                autoResubscribe: true,
                reconnect: {
                    maxAttempts: Infinity,
                    delay: 1000,
                    maxDelay: 30000,
                    factor: 1.5
                }
            });

            // Create subscription client
            this.subsClient = new SubscriptionClient({ transport: this.transport });

            // Check for remote node configuration
            const remoteNodeUrl = localStorage.getItem('remoteNodeUrl');
            const useRemote = localStorage.getItem('useRemoteNode') === 'true';
            
            if (useRemote && remoteNodeUrl) {
                this.remoteNodeUrl = remoteNodeUrl;
                this.useRemoteNode = true;
                // console.log('[POSITION-TRACKER] 🌐 Remote node enabled:', remoteNodeUrl);
            }
            
            // Create PUBLIC API transport
            const publicTransport = new HttpTransport({ timeout: 10000 });
            const publicInfoClient = new InfoClient({ transport: publicTransport });
            
            // Create REMOTE NODE transport if configured
            if (this.useRemoteNode) {
                this.remoteTransport = new HttpTransport({
                    timeout: 10000,
                    server: {
                        mainnet: { api: this.remoteNodeUrl }
                    }
                });
                this.remoteInfoClient = new InfoClient({ transport: this.remoteTransport });
            }
            
            // Wrap in DualInfoClient for transparent routing
            this.infoClient = new DualInfoClient(
                publicInfoClient,
                this.remoteInfoClient,
                this.useRemoteNode
            );

            // Wait for connection
            await this.transport.ready();
            this.isConnected = true;
            // console.log('[POSITION-TRACKER] ✅ WebSocket connected');

            // Load initial data
            await this.loadInitialData();

            // Subscribe to real-time user events
            await this.subscribeToUserData();

            // Start 30-second polling backup (matches Electron)
            this.startOrdersPolling();

        } catch (error) {
            // console.error('[POSITION-TRACKER] ❌ Failed to connect:', error);
            throw error;
        }
    }

    /**
     * Load initial position and order data
     * Matches positions-live.js loadInitialData()
     */
    async loadInitialData() {
        try {
            // console.log('[POSITION-TRACKER] 📊 Loading initial data...');
            
            // Get user state (use BrowserHyperliquidClient which uses same SDK)
            const userState = await window.BrowserHyperliquidClient.getUserState();
            const openOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();

            // Fetch spot balance
            const spotState = await window.BrowserHyperliquidClient.getSpotBalance();
            const usdhBalance = parseFloat(
                spotState?.balances?.find(b => b.coin === 'USDH')?.total || 0
            );
            
            // Cache USDH balance to prevent flickering
            this.cachedUsdhBalance = usdhBalance;
            this.usdhFetched = true; // Mark as fetched, even if value is 0

            // Update account summary
            // Use crossMarginSummary for available balance (correct for isolated margin)
            const crossMargin = userState?.crossMarginSummary || userState?.marginSummary || {};
            const crossAccountValue = parseFloat(crossMargin?.accountValue || 0);
            const crossMarginUsed = parseFloat(crossMargin?.totalMarginUsed || 0);
            const availableForTrading = crossAccountValue - crossMarginUsed;

            this.accountSummary = {
                accountValue: parseFloat(userState?.marginSummary?.accountValue || userState?.accountValue || 0),
                totalRawUsd: parseFloat(userState?.totalRawUsd || 0),
                withdrawable: parseFloat(userState?.withdrawable || 0),
                marginUsed: parseFloat(userState?.marginSummary?.totalMarginUsed || 0),
                totalNtlPos: parseFloat(userState?.totalNtlPos || 0),
                usdhBalance: usdhBalance,
                availableForTrading: availableForTrading
            };
            // Record when we got data from API
            this.lastDataUpdate = Date.now();

            // Process positions
            this.positions.clear();
            if (userState && userState.assetPositions) {
                userState.assetPositions.forEach(assetPos => {
                    const pos = assetPos.position;
                    if (pos && Math.abs(parseFloat(pos.szi)) > 0) {
                        this.positions.set(pos.coin, {
                            coin: pos.coin,
                            size: parseFloat(pos.szi),
                            entryPrice: parseFloat(pos.entryPx || 0),
                            unrealizedPnl: parseFloat(pos.unrealizedPnl || 0),
                            side: parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT',
                            dex: 'main',
                            isHip3: false
                        });
                    }
                });
            }

            // Process orders
            this.orders.clear();
            if (openOrders && Array.isArray(openOrders)) {
                openOrders.forEach(order => {
                    const orderData = {
                        coin: order.coin,
                        side: order.side === 'A' ? 'SELL' : 'BUY',
                        size: parseFloat(order.sz),
                        limitPx: parseFloat(order.limitPx),
                        oid: order.oid,
                        isPositionTpsl: order.isPositionTpsl,
                        reduceOnly: order.reduceOnly || false,
                        cloid: order.cloid || null,
                        timestamp: order.timestamp || Date.now(),
                        dex: 'main',
                        isHip3: order.coin.includes(':')
                    };
                    this.orders.set(order.oid, orderData);
                });
            }

            this.processTPSLOrders();
            // console.log(`[POSITION-TRACKER] ✅ Loaded ${this.positions.size} positions, ${this.orders.size} orders`);
            this.broadcastData();

        } catch (error) {
            // console.error('[POSITION-TRACKER] ❌ Error loading initial data:', error);
        }
    }

    /**
     * Subscribe to user events and fills via WebSocket
     * Matches positions-live.js subscribeToUserData()
     */
    async subscribeToUserData() {
        try {
            // console.log('[POSITION-TRACKER] 📋 Subscribing to user events...');
            
            // Subscribe to user events (fills, cancellations, funding, liquidations)
            await this.subsClient.userEvents(
                { user: this.publicAddress },
                (event) => this.handleUserEvent(event)
            );
            
            // console.log('[POSITION-TRACKER] ✅ Subscribed to user events');
            
            // Subscribe to user fills for real-time position updates
            await this.subsClient.userFills(
                this.publicAddress,
                (fills) => this.handleUserFills(fills)
            );
            
            // console.log('[POSITION-TRACKER] ✅ Subscribed to user fills');
            
            // Subscribe to open orders (real-time Main DEX order updates)
            await this.subsClient.openOrders(
                this.publicAddress,
                (orders) => this.handleOpenOrdersUpdate(orders)
            );
            
            // console.log('[POSITION-TRACKER] ✅ Subscribed to open orders');
            
            // Subscribe to clearinghouse state (real-time Main DEX account balance)
            await this.subsClient.clearinghouseState(
                this.publicAddress,
                (state) => this.handleClearinghouseUpdate(state)
            );
            
            // console.log('[POSITION-TRACKER] ✅ Subscribed to clearinghouse state');
            
        } catch (error) {
            // console.error('[POSITION-TRACKER] ❌ Error subscribing to user events:', error);
        }
    }

    /**
     * Handle user events from WebSocket (fills, cancellations, funding)
     * Matches positions-live.js handleUserEvent()
     */
    handleUserEvent(event) {
        // console.log('[POSITION-TRACKER] 📨 User event:', event.type);
        
        // Handle fills
        if (event.fills && event.fills.length > 0) {
            // console.log(`[POSITION-TRACKER] 💰 ${event.fills.length} fill(s) received`);
            event.fills.forEach(fill => {
                this.updatePositionFromFill(fill);
            });
            // Mark as fresh data - fills are live events
            this.lastDataUpdate = Date.now();
            this.broadcastData();
        }
        
        // Handle order cancellations
        if (event.nonUserCancel && event.nonUserCancel.length > 0) {
            event.nonUserCancel.forEach(cancel => {
                // console.log(`[POSITION-TRACKER] ❌ Order ${cancel.oid} cancelled`);
                this.orders.delete(cancel.oid);
                
                // BYPASS DEBOUNCE: Clear pending state for immediate removal
                this.pendingAdds.delete(cancel.oid);
                this.pendingRemoves.delete(cancel.oid);
                this.previousOrderIds.delete(cancel.oid);
                
                // Emit order-cancelled event
                window.dispatchEvent(new CustomEvent('order-cancelled', {
                    detail: { 
                        orderId: cancel.oid, 
                        reason: 'cancelled-nonuser'
                    }
                }));
            });
            // Mark as fresh data - cancels are live events
            this.lastDataUpdate = Date.now();
            this.broadcastData();
        }
        
        if (event.userCancel && event.userCancel.length > 0) {
            event.userCancel.forEach(cancel => {
                // console.log(`[POSITION-TRACKER] ❌ Order ${cancel.oid} cancelled by user`);
                this.orders.delete(cancel.oid);
                
                // BYPASS DEBOUNCE: Clear pending state for immediate removal
                this.pendingAdds.delete(cancel.oid);
                this.pendingRemoves.delete(cancel.oid);
                this.previousOrderIds.delete(cancel.oid);
                
                // Emit order-cancelled event
                window.dispatchEvent(new CustomEvent('order-cancelled', {
                    detail: { 
                        orderId: cancel.oid, 
                        reason: 'cancelled-user'
                    }
                }));
            });
            // Mark as fresh data - cancels are live events
            this.lastDataUpdate = Date.now();
            this.broadcastData();
        }
    }

    /**
     * Handle user fills stream
     * Matches positions-live.js handleUserFills()
     */
    handleUserFills(fills) {
        if (fills && fills.fills && fills.fills.length > 0) {
            // console.log(`[POSITION-TRACKER] 💹 ${fills.fills.length} new fill(s)`);
            fills.fills.forEach(fill => {
                this.updatePositionFromFill(fill);
            });
            // Mark as fresh data - fills are live events
            this.lastDataUpdate = Date.now();
            this.broadcastData();
        }
    }

    /**
     * Handle clearinghouse state updates from WebSocket (Main DEX real-time account balance + PnL)
     * Updates account summary AND position PnL if values changed
     */
    handleClearinghouseUpdate(state) {
        if (!state) return;
        
        // console.log('[POSITION-TRACKER] 🔔 handleClearinghouseUpdate called');
        // console.log('[POSITION-TRACKER] 📊 assetPositions:', state?.assetPositions?.length || 0);
        
        const newPerpAccountValue = parseFloat(
            state?.marginSummary?.accountValue || 
            state?.accountValue || 
            0
        );
        
        const oldPerpAccountValue = this.accountSummary.accountValue || 0;
        
        // Check if WS value changed (any change, since account value includes PnL)
        const valueChanged = Math.abs(newPerpAccountValue - oldPerpAccountValue) > 0.001;
        
        // Also check if we have position data to update
        const hasPositionData = state?.assetPositions && Array.isArray(state.assetPositions);
        
        // console.log('[POSITION-TRACKER] 🔍 valueChanged:', valueChanged, 'hasPositionData:', hasPositionData);
        
        // Broadcast if account changed OR we have position data
        // Account value includes unrealized PnL, so any change indicates PnL movement
        if (valueChanged || hasPositionData) {
            // Update account summary if value changed
            if (valueChanged) {
                this.wsAccountSummary = {
                    perpAccountValue: newPerpAccountValue,
                    totalRawUsd: parseFloat(state?.totalRawUsd || 0),
                    withdrawable: parseFloat(state?.withdrawable || 0),
                    marginUsed: parseFloat(state?.marginSummary?.totalMarginUsed || 0),
                    totalNtlPos: parseFloat(state?.totalNtlPos || 0)
                };
                
                this.lastWsAccountUpdate = Date.now();
                this.useApiAsSource = false;
                
                // Merge with existing accountSummary (keep spot value from API)
                // Defensive merge: Only update USDH if we've fetched it from API at least once
                // This allows legitimate $0 balances while preventing flicker from uninitialized cache
                // Note: WS doesn't provide crossMarginSummary, use marginSummary values
                // (accurate for cross-only accounts, API poll corrects for isolated margin)
                const wsAvailableForTrading = this.wsAccountSummary.perpAccountValue - this.wsAccountSummary.marginUsed;
                this.accountSummary = {
                    ...this.accountSummary,
                    accountValue: this.wsAccountSummary.perpAccountValue,
                    totalRawUsd: this.wsAccountSummary.totalRawUsd,
                    withdrawable: this.wsAccountSummary.withdrawable,
                    marginUsed: this.wsAccountSummary.marginUsed,
                    totalNtlPos: this.wsAccountSummary.totalNtlPos,
                    usdhBalance: this.usdhFetched ? this.cachedUsdhBalance : (this.accountSummary.usdhBalance || 0),
                    availableForTrading: this.accountSummary.availableForTrading ?? wsAvailableForTrading
                };
            }
            
            // DISABLED: WS PnL updates (use polling only for accurate PnL)
            // WS often sends unrealizedPnl: 0 for new positions
            // 2-second polling provides fresher, more accurate PnL data
            // if (hasPositionData) {
            //     console.log('[POSITION-TRACKER] 📥 Processing', state.assetPositions.length, 'positions from WS');
            //     state.assetPositions.forEach(assetPos => {
            //         const pos = assetPos.position;
            //         if (pos && pos.coin) {
            //             const existingPosition = this.positions.get(pos.coin);
            //             if (existingPosition && existingPosition.dex === 'main') {
            //                 // Update existing position PnL
            //                 existingPosition.unrealizedPnl = parseFloat(pos.unrealizedPnl || 0);
            //             } else if (!existingPosition && Math.abs(parseFloat(pos.szi)) > 0) {
            //                 // Add new position immediately from WebSocket
            //                 this.positions.set(pos.coin, {
            //                     coin: pos.coin,
            //                     size: parseFloat(pos.szi),
            //                     entryPrice: parseFloat(pos.entryPx || 0),
            //                     unrealizedPnl: parseFloat(pos.unrealizedPnl || 0),
            //                     side: parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT',
            //                     dex: 'main',
            //                     isHip3: false
            //                 });
            //             }
            //         }
            //     });
            // }
            
            this.lastDataUpdate = Date.now();
            // console.log(`[POSITION-TRACKER] 💰 WS update complete, broadcasting...`);
            this.broadcastData();
        } else {
            // console.log('[POSITION-TRACKER] ⏭️ No changes, skipping broadcast');
        }
    }
    
    /**
     * Handle open orders updates from WebSocket (Main DEX real-time)
     * Updates only if order list changed - HIP-3 orders preserved from API polls
     */
    handleOpenOrdersUpdate(ordersData) {
        // Handle both snapshot and streaming formats
        const orders = ordersData?.isSnapshot === true 
            ? ordersData.orders 
            : ordersData;
        
        if (!Array.isArray(orders)) return;
        
        // Check if orders actually changed
        const newOrderIds = new Set(orders.map(o => o.oid));
        const existingMainDexIds = new Set(
            Array.from(this.orders.values())
                .filter(o => o.dex === 'main')
                .map(o => o.oid)
        );
        
        const hasChanges = 
            newOrderIds.size !== existingMainDexIds.size ||
            ![...newOrderIds].every(id => existingMainDexIds.has(id));
        
        if (hasChanges || ordersData?.isSnapshot === true) {
            // Orders changed - use WS data for Main DEX only
            this.wsOrdersMap.clear();
            orders.forEach(order => {
                const orderData = {
                    coin: order.coin,
                    side: order.side === 'A' ? 'SELL' : 'BUY',
                    size: parseFloat(order.sz),
                    limitPx: parseFloat(order.limitPx),
                    oid: order.oid,
                    cloid: order.cloid,
                    timestamp: order.timestamp,
                    reduceOnly: order.reduceOnly || false,
                    isPositionTpsl: order.isPositionTpsl || false,
                    triggerCondition: order.triggerCondition,
                    triggerPx: order.triggerPx ? parseFloat(order.triggerPx) : null,
                    orderType: order.orderType,
                    dex: 'main',
                    isHip3: order.coin.includes(':')
                };
                this.wsOrdersMap.set(order.oid, orderData);
            });
            
            this.lastWsOrdersUpdate = Date.now();
            
            // Remove only Main DEX orders, preserve HIP-3 orders from other DEXs
            for (const [oid, order] of this.orders.entries()) {
                if (order.dex === 'main') {
                    this.orders.delete(oid);
                }
            }
            
            // Add fresh Main DEX orders from WS
            this.wsOrdersMap.forEach((order, oid) => {
                this.orders.set(oid, order);
            });
            
            this.processTPSLOrders();
            
            this.lastDataUpdate = Date.now();
            // console.log(`[POSITION-TRACKER] 📋 WS orders update: ${orders.length} orders`);
            this.broadcastData();
        }
        // Else: No changes, keep using existing data
    }

    /**
     * Update position from fill event
     * Matches positions-live.js updatePositionFromFill()
     */
    updatePositionFromFill(fill) {
        const coin = fill.coin;
        const size = parseFloat(fill.sz);
        const price = parseFloat(fill.px);
        const side = fill.side; // 'B' for bid/buy, 'A' for ask/sell
        
        // Track if order was in our orders map before fill
        const wasTracked = fill.oid ? this.orders.has(fill.oid) : false;
        
        if (!this.positions.has(coin)) {
            // Determine DEX from coin name (HIP-3 coins contain ':')
            const isHip3 = coin.includes(':');
            const dex = isHip3 ? coin.split(':')[0] : 'main';
            
            this.positions.set(coin, {
                coin: coin,
                size: 0,
                entryPrice: 0,
                unrealizedPnl: 0,
                side: null,
                dex: dex,
                isHip3: isHip3
            });
        }
        
        const position = this.positions.get(coin);
        
        // Update position based on fill side
        if (side === 'B') { // Bid = Buy
            if (position.size >= 0) {
                const totalValue = (position.size * position.entryPrice) + (size * price);
                position.size += size;
                position.entryPrice = position.size > 0 ? totalValue / position.size : 0;
                position.side = 'LONG';
            } else {
                position.size += size;
                if (position.size > 0) {
                    position.entryPrice = price;
                    position.side = 'LONG';
                } else if (position.size === 0) {
                    position.side = null;
                }
            }
        } else { // Ask = Sell
            if (position.size <= 0) {
                const totalValue = (Math.abs(position.size) * position.entryPrice) + (size * price);
                position.size -= size;
                position.entryPrice = position.size < 0 ? totalValue / Math.abs(position.size) : 0;
                position.side = 'SHORT';
            } else {
                position.size -= size;
                if (position.size < 0) {
                    position.entryPrice = price;
                    position.side = 'SHORT';
                } else if (position.size === 0) {
                    position.side = null;
                }
            }
        }
        
        // Clean up zero positions
        if (position.size === 0) {
            this.positions.delete(coin);
        }
        
        // Emit order-filled event if order was tracked and is now gone
        if (wasTracked && fill.oid && !this.orders.has(fill.oid)) {
            // console.log(`[POSITION-TRACKER] ✅ Order ${fill.oid} completely filled, emitting event`);
            
            // BYPASS DEBOUNCE: Clear pending state for immediate removal
            this.pendingAdds.delete(fill.oid);
            this.pendingRemoves.delete(fill.oid);
            this.previousOrderIds.delete(fill.oid);
            
            window.dispatchEvent(new CustomEvent('order-filled', {
                detail: { 
                    orderId: fill.oid, 
                    reason: 'filled',
                    fill: fill
                }
            }));
        }
    }

    /**
     * Start polling for orders and positions (backup to WebSocket)
     * Uses dynamic interval: 2s for local node, 30s when using public API fallback
     */
    startOrdersPolling() {
        // Get initial interval from infoClient (defaults to 2000 if not available)
        const initialInterval = this.infoClient?.getPollingInterval?.() || 2000;
        // console.log(`[POSITION-TRACKER] 🔄 Starting polling (interval: ${initialInterval}ms)`);
        
        // Subscribe to fallback state changes for dynamic interval adjustment
        if (this.infoClient?.onFallbackChange) {
            this.fallbackUnsubscribe = this.infoClient.onFallbackChange((isUsingFallback, newInterval) => {
                // console.log(`[POSITION-TRACKER] 📡 Fallback state changed: ${isUsingFallback ? 'FALLBACK MODE' : 'NORMAL MODE'}, new interval: ${newInterval}ms`);
                
                // DISABLE debounce in fallback mode (30s x 2 polls = 60s wait is too long)
                this.debounceEnabled = !isUsingFallback;
                
                // Clear pending changes when switching modes to prevent stale state
                this.pendingAdds.clear();
                this.pendingRemoves.clear();
                
                this._restartPollingWithInterval(newInterval);
            });
        }
        
        this._startPollingInterval(initialInterval);
    }
    
    /**
     * Start polling with specified interval
     * @private
     */
    _startPollingInterval(intervalMs) {
        this.currentPollingInterval = intervalMs;
        
        this.pollInterval = setInterval(async () => {
            try {
                // Check if data is stale (> 60 seconds old)
                const dataAge = this.lastDataUpdate ? Date.now() - this.lastDataUpdate : Infinity;
                const isStale = dataAge > 60000; // 1 minute
                
                if (isStale) {
                    // console.warn(`[POSITION-TRACKER] ⚠️ Data is stale (${Math.floor(dataAge / 1000)}s old), forcing poll...`);
                }
                
                // Always poll on schedule, but log if data was stale
                await this.pollData();
                
            } catch (error) {
                // console.error('[POSITION-TRACKER] ❌ Polling error:', error);
            }
        }, intervalMs);
    }
    
    /**
     * Restart polling with new interval (called when fallback state changes)
     * @private
     */
    _restartPollingWithInterval(newIntervalMs) {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this._startPollingInterval(newIntervalMs);
        // console.log(`[POSITION-TRACKER] 🔄 Polling restarted with ${newIntervalMs}ms interval`);
    }

    /**
     * Stop position tracking and close WebSocket
     */
    async stop() {
        // console.log('[POSITION-TRACKER] Stopping...');
        
        // Unsubscribe from fallback state changes
        if (this.fallbackUnsubscribe) {
            this.fallbackUnsubscribe();
            this.fallbackUnsubscribe = null;
        }
        
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        
        if (this.transport) {
            await this.transport.close();
            this.transport = null;
        }
        
        this.subsClient = null;
        this.isRunning = false;
        this.isConnected = false;
        // console.log('[POSITION-TRACKER] ✅ Stopped');
    }

    /**
     * Poll position and order data (backup to WebSocket, runs every 30 seconds)
     * Matches positions-live.js pollOpenOrders() + pollPositionPnL()
     */
    async pollData() {
        // Mutex: If already polling, wait for it to finish then execute
        if (this.isPolling) {
            await new Promise((resolve) => {
                this.pollQueue.push(resolve);
            });
            // Now that previous poll finished, run this poll
            // (recursive call will acquire mutex since isPolling is now false)
            return this.pollData();
        }
        
        this.isPolling = true;
        const pollStartTime = Date.now();
        const maxRetries = 3;
        let lastError = null;
        
        try {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                // Refresh DEX list every 5 minutes or if not cached
                const now = Date.now();
                if (!this.allKnownDexs || !this.dexsCacheTime || (now - this.dexsCacheTime) > 300000) {
                    this.allKnownDexs = await this.infoClient.perpDexs();
                    this.dexsCacheTime = now;
                }
                const allDexs = this.allKnownDexs;
                
                // Separate Main DEX from HIP-3 DEXs
                const mainDex = allDexs.find(d => d === null); // null = main
                const hip3Dexs = allDexs.filter(d => d !== null);
                
                // Select DEXs to query this poll: Main + one rotating HIP-3
                const dexsToQuery = [mainDex];
                if (hip3Dexs.length > 0) {
                    const selectedHip3 = hip3Dexs[this.dexRotationIndex % hip3Dexs.length];
                    dexsToQuery.push(selectedHip3);
                    // console.log('[POSITION-TRACKER] 🔍 Rotation poll querying DEXs:', {
                    //     main: 'main',
                    //     hip3: selectedHip3?.name || 'unknown',
                    //     rotationIndex: this.dexRotationIndex
                    // });
                    this.dexRotationIndex++; // Increment for next poll
                }
                
                // Query user states for selected DEXs only
                const allStates = [];
                for (const dex of dexsToQuery) {
                    const dexName = dex === null ? '' : dex.name;
                    try {
                        const userState = await this.infoClient.clearinghouseState({ 
                            user: this.publicAddress, 
                            dex: dexName 
                        });
                        allStates.push({ dex: dexName || 'main', state: userState });
                    } catch (error) {
                        // console.warn(`[POSITION-TRACKER] Failed to get state for ${dexName || 'main'} DEX:`, error.message);
                    }
                }
                
                // Query orders for ALL DEXs
                const allOrders = [];
                for (const dex of dexsToQuery) {
                    const dexName = dex === null ? '' : dex.name;
                    try {
                        const orders = await this.infoClient.openOrders({ 
                            user: this.publicAddress, 
                            dex: dexName 
                        });
                        if (orders && Array.isArray(orders)) {
                            orders.forEach(order => {
                                allOrders.push({
                                    ...order,
                                    dex: dexName || 'main',
                                    isHip3: order.coin.includes(':')
                                });
                            });
                        }
                    } catch (error) {
                        // console.warn(`[POSITION-TRACKER] Failed to get orders for ${dexName || 'main'} DEX:`, error.message);
                    }
                }

                // Combine account summary from Main DEX (primary account value)
                const mainState = allStates.find(s => s.dex === 'main')?.state;
                // Fetch spot balance
                const spotState = await window.BrowserHyperliquidClient.getSpotBalance();
                const usdhBalance = parseFloat(
                    spotState?.balances?.find(b => b.coin === 'USDH')?.total || 0
                );
                
                // Cache USDH balance to prevent flickering
                this.cachedUsdhBalance = usdhBalance;
                this.usdhFetched = true; // Mark as fetched, even if value is 0

                const apiPerpAccountValue = parseFloat(mainState?.marginSummary?.accountValue || mainState?.accountValue || 0);
                
                // Check WS data freshness
                const wsAge = Date.now() - this.lastWsAccountUpdate;
                const wsStale = wsAge > 30000; // 30 seconds
                
                // Smart merge: Use WS if fresh, else use API
                // Calculate availableForTrading from crossMarginSummary (correct for isolated margin)
                const crossMargin = mainState?.crossMarginSummary || mainState?.marginSummary || {};
                const crossAccountValue = parseFloat(crossMargin?.accountValue || 0);
                const crossMarginUsed = parseFloat(crossMargin?.totalMarginUsed || 0);
                const availableForTrading = crossAccountValue - crossMarginUsed;

                if (wsStale || !this.wsAccountSummary) {
                    // Use API as primary source (WS stale or unavailable)
                    this.accountSummary = {
                        accountValue: apiPerpAccountValue,
                        totalRawUsd: parseFloat(mainState?.totalRawUsd || 0),
                        withdrawable: parseFloat(mainState?.withdrawable || 0),
                        marginUsed: parseFloat(mainState?.marginSummary?.totalMarginUsed || 0),
                        totalNtlPos: parseFloat(mainState?.totalNtlPos || 0),
                        usdhBalance: usdhBalance, // Just fetched from API above
                        availableForTrading: availableForTrading
                    };
                    this.useApiAsSource = true;
                    // console.log(`[POSITION-TRACKER] Using API for account (WS age: ${Math.floor(wsAge/1000)}s)`);
                } else {
                    // Merge: Use WS for perp data, API for spot data
                    // For availableForTrading, prefer API's crossMarginSummary (more accurate for isolated)
                    this.accountSummary = {
                        accountValue: this.wsAccountSummary.perpAccountValue || apiPerpAccountValue,
                        totalRawUsd: this.wsAccountSummary.totalRawUsd,
                        withdrawable: this.wsAccountSummary.withdrawable,
                        marginUsed: this.wsAccountSummary.marginUsed,
                        totalNtlPos: this.wsAccountSummary.totalNtlPos,
                        usdhBalance: usdhBalance,  // Always from API
                        availableForTrading: availableForTrading  // Use API's crossMarginSummary
                    };
                    this.useApiAsSource = false;
                    // console.log(`[POSITION-TRACKER] Using WS for account (age: ${Math.floor(wsAge/1000)}s)`);
                }
                
                // Timestamp check: Only reject poll if WS updated WITH POSITION DATA during fetch
                // REMOVED: Don't exit poll based on WS updates
                // WS position updates are DISABLED - polling is the ONLY source for position PnL
                // Always let poll complete to ensure fresh PnL data every 2 seconds

                // Record when we got FRESH data from API
                this.lastDataUpdate = Date.now();
                this.isConnected = true;

                // MERGE positions (only update queried DEXs, preserve others)
                // Build set of DEXs we queried
                const queriedDexNames = new Set(allStates.map(s => s.dex));
                
                // Cache old PnL values before deletion to prevent flash
                const cachedPnL = new Map();
                for (const [coin, pos] of this.positions.entries()) {
                    if (queriedDexNames.has(pos.dex)) {
                        cachedPnL.set(coin, pos.unrealizedPnl);
                    }
                }
                
                // Remove positions from queried DEXs only
                for (const [coin, pos] of this.positions.entries()) {
                    if (queriedDexNames.has(pos.dex)) {
                        this.positions.delete(coin);
                    }
                }
                
                // Add fresh positions from queried DEXs
                // CRITICAL: Only add/update if position is NOT already in map from a different DEX
                // This prevents cross-DEX API responses from overwriting the correct DEX
                allStates.forEach(({ dex, state }) => {
                    // console.log(`[POSITION-TRACKER] 🔍 Processing DEX: ${dex}, has positions: ${state?.assetPositions?.length || 0}`);
                    if (state && state.assetPositions) {
                        state.assetPositions.forEach(assetPos => {
                            const pos = assetPos.position;
                            if (pos && Math.abs(parseFloat(pos.szi)) > 0) {
                                // Check if position already exists from a non-queried DEX
                                const existingPos = this.positions.get(pos.coin);
                                if (existingPos && !queriedDexNames.has(existingPos.dex)) {
                                    // Position exists from a DEX we didn't query - preserve it, don't overwrite!
                                    return;
                                }
                                
                                const isHip3 = pos.coin.includes(':');
                                const newPnl = parseFloat(pos.unrealizedPnl || 0);
                                const oldPnl = cachedPnL.get(pos.coin);
                                
                                // Use cached PnL if API returns 0 but we had a valid PnL before
                                const finalPnl = (newPnl === 0 && oldPnl && Math.abs(oldPnl) > 0.01) ? oldPnl : newPnl;
                                
                                // console.log(`[POSITION-TRACKER] 📍 Position found: ${pos.coin} (size: ${pos.szi}) on ${dex} DEX, isHip3: ${isHip3}`);
                                this.positions.set(pos.coin, {
                                    coin: pos.coin,
                                    size: parseFloat(pos.szi),
                                    entryPrice: parseFloat(pos.entryPx || 0),
                                    unrealizedPnl: finalPnl,
                                    side: parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT',
                                    dex: dex,
                                    isHip3: isHip3
                                });
                            }
                        });
                    }
                });

                // MERGE orders (only update queried DEXs, preserve others)
                // Check WS orders freshness (Main DEX only)
                const ordersAge = Date.now() - this.lastWsOrdersUpdate;
                const ordersStale = ordersAge > 30000; // 30 seconds
                
                if (ordersStale || this.wsOrdersMap.size === 0) {
                    // Use API orders (WS stale or unavailable)
                    // Remove orders from queried DEXs only
                    for (const [oid, order] of this.orders.entries()) {
                        if (queriedDexNames.has(order.dex)) {
                            this.orders.delete(oid);
                        }
                    }
                    
                    // Add fresh orders from queried DEXs
                    // CRITICAL: Only add/update if order is NOT already in map from a different DEX
                    // This prevents cross-DEX API responses from overwriting the correct DEX
                    if (allOrders && Array.isArray(allOrders)) {
                        allOrders.forEach(order => {
                            // Check if order already exists from a non-queried DEX
                            const existingOrder = this.orders.get(order.oid);
                            if (existingOrder && !queriedDexNames.has(existingOrder.dex)) {
                                // Order exists from a DEX we didn't query - preserve it, don't overwrite!
                                // This prevents API cross-listing from changing the DEX assignment
                                return;
                            }
                            
                            const orderData = {
                                coin: order.coin,
                                side: order.side === 'A' ? 'SELL' : 'BUY',
                                size: parseFloat(order.sz),
                                limitPx: parseFloat(order.limitPx),
                                oid: order.oid,
                                isPositionTpsl: order.isPositionTpsl,
                                reduceOnly: order.reduceOnly || false,
                                cloid: order.cloid || null,
                                timestamp: order.timestamp || Date.now(),
                                dex: order.dex || 'main',
                                isHip3: order.isHip3 || order.coin.includes(':')
                            };
                            this.orders.set(order.oid, orderData);
                        });
                    }
                    // console.log(`[POSITION-TRACKER] Using API for orders (WS age: ${Math.floor(ordersAge/1000)}s)`);
                } else {
                    // Use WS for Main DEX, keep HIP-3 from previous poll
                    // Remove orders from queried DEXs only (both Main and HIP-3 this cycle)
                    for (const [oid, order] of this.orders.entries()) {
                        if (queriedDexNames.has(order.dex)) {
                            this.orders.delete(oid);
                        }
                    }
                    
                    // Add Main DEX orders from WS
                    this.wsOrdersMap.forEach((order, oid) => {
                        this.orders.set(oid, order);
                    });
                    
                    // Add HIP-3 orders from API poll (for the queried HIP-3 DEX this rotation)
                    if (allOrders && Array.isArray(allOrders)) {
                        allOrders.filter(o => o.isHip3 || o.coin.includes(':')).forEach(order => {
                            const orderData = {
                                coin: order.coin,
                                side: order.side === 'A' ? 'SELL' : 'BUY',
                                size: parseFloat(order.sz),
                                limitPx: parseFloat(order.limitPx),
                                oid: order.oid,
                                isPositionTpsl: order.isPositionTpsl,
                                reduceOnly: order.reduceOnly || false,
                                cloid: order.cloid || null,
                                timestamp: order.timestamp || Date.now(),
                                dex: order.dex || 'main',
                                isHip3: true
                            };
                            this.orders.set(order.oid, orderData);
                        });
                    }
                    // console.log(`[POSITION-TRACKER] Using WS for orders (age: ${Math.floor(ordersAge/1000)}s)`);
                }

                // Process TP/SL orders (matches positions-live.js logic)
                this.processTPSLOrders();

                // SUCCESS - Send fresh data to all views
                this.broadcastData();
                
                // Log success if this was a retry
                if (attempt > 1) {
                    // console.log(`[POSITION-TRACKER] ✅ Poll succeeded on attempt ${attempt}`);
                }
                
                const hip3PositionCount = Array.from(this.positions.values()).filter(p => p.isHip3).length;
                const hip3OrderCount = Array.from(this.orders.values()).filter(o => o.coin.includes(':')).length;
                
                // console.log(`[POSITION-TRACKER] ✅ Loaded ${this.positions.size} positions from all DEXs (${hip3PositionCount} HIP-3)`);
                // console.log(`[POSITION-TRACKER] ✅ Loaded ${this.orders.size} orders from all DEXs (${hip3OrderCount} HIP-3)`);
                
                return; // Exit successfully

            } catch (error) {
                lastError = error;
                // console.error(`[POSITION-TRACKER] ❌ Poll attempt ${attempt}/${maxRetries} failed:`, error.message);
                
                // If not last attempt, wait before retry with exponential backoff
                if (attempt < maxRetries) {
                    // Detect 429 rate limit errors
                    const is429 = error.message?.includes('429') || 
                                 error.message?.includes('Too Many Requests') ||
                                 error.status === 429;
                    
                    let retryDelay;
                    if (is429) {
                        // Exponential backoff for rate limits: 5s, 15s, 45s (max 60s)
                        retryDelay = Math.min(5000 * Math.pow(3, attempt - 1), 60000);
                        // console.warn(`[POSITION-TRACKER] ⚠️ 429 Rate limit detected, waiting ${retryDelay}ms before retry ${attempt + 1}/${maxRetries}`);
                    } else {
                        // Regular errors: 2s, 4s, 8s (max 10s)
                        retryDelay = Math.min(2000 * Math.pow(2, attempt - 1), 10000);
                        // console.log(`[POSITION-TRACKER] ⏳ Retrying in ${retryDelay}ms...`);
                    }
                    
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }
        }
        
        // All retries failed - broadcast stale data with disconnected flag
        // console.error(`[POSITION-TRACKER] ❌ All ${maxRetries} poll attempts failed. Last error:`, lastError);
        this.isConnected = false;
        
        // Still broadcast last known data so UI shows something (with stale warning)
        this.broadcastData();
        
        } finally {
            // Release mutex and resolve queued requests
            this.isPolling = false;
            while (this.pollQueue.length > 0) {
                this.pollQueue.shift()();
            }
        }
    }

    /**
     * Poll ALL DEXs (no rotation) - for immediate full refresh
     * Use after user actions (place order, cancel, close) for instant feedback
     * Includes mutex and timestamp protection against race conditions
     */
    async pollAllDexs() {
        // Mutex: If already polling, wait for it to finish then execute
        if (this.isPolling) {
            await new Promise((resolve) => {
                this.pollQueue.push(resolve);
            });
            // Now that previous poll finished, run this poll
            // (recursive call will acquire mutex since isPolling is now false)
            return this.pollAllDexs();
        }
        
        this.isPolling = true;
        const pollStartTime = Date.now();
        const maxRetries = 3;
        let lastError = null;
        
        try {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                // Refresh DEX list every 5 minutes or if not cached
                const now = Date.now();
                if (!this.allKnownDexs || !this.dexsCacheTime || (now - this.dexsCacheTime) > 300000) {
                    this.allKnownDexs = await this.infoClient.perpDexs();
                    this.dexsCacheTime = now;
                }
                const allDexs = this.allKnownDexs;
                
                // Query ALL DEXs (no rotation)
                const allStates = [];
                for (const dex of allDexs) {
                    const dexName = dex === null ? '' : dex.name;
                    try {
                        const userState = await this.infoClient.clearinghouseState({ 
                            user: this.publicAddress, 
                            dex: dexName 
                        });
                        allStates.push({ dex: dexName || 'main', state: userState });
                    } catch (error) {
                        // console.warn(`[POSITION-TRACKER] Failed to get state for ${dexName || 'main'} DEX:`, error.message);
                    }
                }
                
                // Query orders for ALL DEXs
                const allOrders = [];
                for (const dex of allDexs) {
                    const dexName = dex === null ? '' : dex.name;
                    try {
                        const orders = await this.infoClient.openOrders({ 
                            user: this.publicAddress, 
                            dex: dexName 
                        });
                        if (orders && Array.isArray(orders)) {
                            orders.forEach(order => {
                                allOrders.push({
                                    ...order,
                                    dex: dexName || 'main',
                                    isHip3: order.coin.includes(':')
                                });
                            });
                        }
                    } catch (error) {
                        // console.warn(`[POSITION-TRACKER] Failed to get orders for ${dexName || 'main'} DEX:`, error.message);
                    }
                }

                // Combine account summary from Main DEX
                const mainState = allStates.find(s => s.dex === 'main')?.state;
                const spotState = await window.BrowserHyperliquidClient.getSpotBalance();
                const usdhBalance = parseFloat(
                    spotState?.balances?.find(b => b.coin === 'USDH')?.hold || '0'
                );
                
                // Cache USDH balance to prevent flickering
                this.cachedUsdhBalance = usdhBalance;
                this.usdhFetched = true; // Mark as fetched, even if value is 0

                // Calculate availableForTrading from crossMarginSummary (correct for isolated margin)
                const crossMargin = mainState?.crossMarginSummary || mainState?.marginSummary || {};
                const crossAccountValue = parseFloat(crossMargin?.accountValue || 0);
                const crossMarginUsed = parseFloat(crossMargin?.totalMarginUsed || 0);
                const availableForTrading = crossAccountValue - crossMarginUsed;

                this.accountSummary = {
                    accountValue: parseFloat(mainState?.marginSummary?.accountValue || mainState?.accountValue || 0),
                    totalRawUsd: parseFloat(mainState?.totalRawUsd || 0),
                    withdrawable: parseFloat(mainState?.withdrawable || 0),
                    marginUsed: parseFloat(mainState?.marginSummary?.totalMarginUsed || 0),
                    totalNtlPos: parseFloat(mainState?.totalNtlPos || 0),
                    usdhBalance: usdhBalance,
                    availableForTrading: availableForTrading
                };
                
                // REMOVED: WS position check - WS position updates are DISABLED (line 472-498)
                // WS only updates account balance, not positions
                // This check was blocking polls after position closes since WS fires on balance change
                // Always process API data to ensure closed positions disappear immediately
                
                // const wsUpdatedPositions = this.lastDataUpdate && 
                //                           this.lastDataUpdate > pollStartTime &&
                //                           this.lastWsAccountUpdate && 
                //                           (Date.now() - this.lastWsAccountUpdate) < 1000;
                // 
                // if (wsUpdatedPositions) {
                //     // console.warn('[POSITION-TRACKER] 🔥 WebSocket updated positions during pollAllDexs, keeping WS data');
                //     return; // Exit early, keep WebSocket data
                // }

                // Record when we got FRESH data from API
                this.lastDataUpdate = Date.now();
                this.isConnected = true;

                // Cache old PnL values before clearing to prevent flash
                const cachedPnL = new Map();
                for (const [coin, pos] of this.positions.entries()) {
                    cachedPnL.set(coin, pos.unrealizedPnl);
                }

                // Process positions from ALL DEXs
                this.positions.clear();
                allStates.forEach(({ dex, state }) => {
                    if (state && state.assetPositions) {
                        state.assetPositions.forEach(assetPos => {
                            const pos = assetPos.position;
                            if (pos && Math.abs(parseFloat(pos.szi)) > 0) {
                                // Only set if position doesn't already exist (first DEX wins)
                                // This prevents cross-DEX API responses from overwriting with wrong DEX
                                if (!this.positions.has(pos.coin)) {
                                    const isHip3 = pos.coin.includes(':');
                                    const newPnl = parseFloat(assetPos.unrealizedPnl || 0);
                                    const oldPnl = cachedPnL.get(pos.coin);
                                    
                                    // Use cached PnL if API returns 0 but we had a valid PnL before
                                    const finalPnl = (newPnl === 0 && oldPnl && Math.abs(oldPnl) > 0.01) ? oldPnl : newPnl;
                                    
                                    this.positions.set(pos.coin, {
                                        coin: pos.coin,
                                        size: parseFloat(pos.szi),
                                        entryPrice: parseFloat(pos.entryPx || 0),
                                        unrealizedPnl: finalPnl,
                                        side: parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT',
                                        dex: dex,
                                        isHip3: isHip3
                                    });
                                }
                            }
                        });
                    }
                });

                // Process orders from ALL DEXs
                this.orders.clear();
                if (allOrders && Array.isArray(allOrders)) {
                    allOrders.forEach(order => {
                        // Only set if order doesn't already exist (first DEX wins)
                        // This prevents cross-DEX API responses from overwriting with wrong DEX
                        if (!this.orders.has(order.oid)) {
                            const orderData = {
                                coin: order.coin,
                                side: order.side === 'A' ? 'SELL' : 'BUY',
                                size: parseFloat(order.sz),
                                limitPx: parseFloat(order.limitPx),
                                oid: order.oid,
                                isPositionTpsl: order.isPositionTpsl,
                                reduceOnly: order.reduceOnly || false,
                                cloid: order.cloid || null,
                                timestamp: order.timestamp || Date.now(),
                                dex: order.dex || 'main',
                                isHip3: order.isHip3 || order.coin.includes(':')
                            };
                            this.orders.set(order.oid, orderData);
                        }
                    });
                }

                // Process TP/SL orders
                this.processTPSLOrders();

                // SUCCESS - Send fresh data to all views
                this.broadcastData();
                
                const hip3PositionCount = Array.from(this.positions.values()).filter(p => p.isHip3).length;
                const hip3OrderCount = Array.from(this.orders.values()).filter(o => o.coin.includes(':')).length;
                
                // console.log(`[POSITION-TRACKER] ✅ pollAllDexs: ${this.positions.size} positions (${hip3PositionCount} HIP-3), ${this.orders.size} orders (${hip3OrderCount} HIP-3)`);
                
                return; // Exit successfully

            } catch (error) {
                lastError = error;
                // console.error(`[POSITION-TRACKER] ❌ pollAllDexs attempt ${attempt}/${maxRetries} failed:`, error.message);
                
                // If not last attempt, wait before retry
                if (attempt < maxRetries) {
                    const is429 = error.message?.includes('429') || 
                                 error.message?.includes('Too Many Requests') ||
                                 error.status === 429;
                    
                    let retryDelay;
                    if (is429) {
                        retryDelay = Math.min(5000 * Math.pow(3, attempt - 1), 60000);
                        // console.warn(`[POSITION-TRACKER] ⚠️ 429 Rate limit on pollAllDexs, waiting ${retryDelay}ms`);
                    } else {
                        retryDelay = Math.min(2000 * Math.pow(2, attempt - 1), 10000);
                    }
                    
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }
        }
        
        // All retries failed
        // console.error(`[POSITION-TRACKER] ❌ pollAllDexs failed after ${maxRetries} attempts`);
        this.isConnected = false;
        this.broadcastData();
        
        } finally {
            // Release mutex and resolve queued requests
            this.isPolling = false;
            while (this.pollQueue.length > 0) {
                this.pollQueue.shift()();
            }
        }
    }

    /**
     * Process orders to separate entry orders from TP/SL orders
     * Matches positions-live.js processTPSLOrders() logic
     */
    processTPSLOrders() {
        this.entryOrders.clear();
        this.tpslOrders.clear();

        for (const [oid, order] of this.orders) {
            if (order.isPositionTpsl) {
                // TP/SL order - match to position by coin
                const position = this.positions.get(order.coin);
                if (position) {
                    if (!this.tpslOrders.has(order.coin)) {
                        this.tpslOrders.set(order.coin, { tp: null, sl: null });
                    }

                    const tpsl = this.tpslOrders.get(order.coin);

                    // Determine if TP or SL based on price vs position
                    const isLong = position.side === 'LONG';
                    const isProfitable = isLong ?
                        (order.limitPx > position.entryPrice) :
                        (order.limitPx < position.entryPrice);

                    if (isProfitable) {
                        tpsl.tp = order.limitPx;
                    } else {
                        tpsl.sl = order.limitPx;
                    }
                }
            } else {
                // Regular entry order
                this.entryOrders.set(oid, order);
            }
        }
    }

    /**
     * Broadcast data to all views
     * Matches positions-live.js sendToDashboard() format EXACTLY
     */
    broadcastData() {
        try {
            // console.log('[POSITION-TRACKER] 📤 broadcastData() called, positions count:', this.positions.size);
            
            // Add TP/SL data to positions (matches Electron format)
            const positionsArray = Array.from(this.positions.values()).map(position => {
                const tpsl = this.tpslOrders.get(position.coin);
                // console.log(`[POSITION-TRACKER] 📦 Packaging ${position.coin}: PnL=${position.unrealizedPnl}`);
                return {
                    ...position,
                    tp: tpsl?.tp || null,
                    sl: tpsl?.sl || null
                };
            });

            // Apply order debounce logic (2-poll confirmation to prevent flickering)
            let ordersArray;
            const currentOrderIds = new Set(this.orders.keys());
            
            if (!this.debounceEnabled || this.previousOrderIds.size === 0) {
                // CASE 1: Debounce disabled (fallback mode) OR initial load
                // Show all orders immediately
                ordersArray = Array.from(this.orders.values());
                this.previousOrderIds = new Set(currentOrderIds);
                this.pendingAdds.clear();
                this.pendingRemoves.clear();
            } else {
                // CASE 2: Normal mode (2s polling) - apply 2-poll confirmation
                
                // Calculate diffs
                const added = [...currentOrderIds].filter(id => !this.previousOrderIds.has(id));
                const removed = [...this.previousOrderIds].filter(id => !currentOrderIds.has(id));
                const unchanged = [...currentOrderIds].filter(id => this.previousOrderIds.has(id));
                
                // Process ADDED orders
                for (const oid of added) {
                    if (this.pendingAdds.has(oid)) {
                        // Increment poll count
                        const pending = this.pendingAdds.get(oid);
                        pending.pollCount++;
                        
                        if (pending.pollCount >= this.DEBOUNCE_POLL_COUNT) {
                            // CONFIRMED - will be included in broadcast
                            this.pendingAdds.delete(oid);
                        }
                    } else {
                        // First time seeing this order - mark as pending (don't show yet)
                        this.pendingAdds.set(oid, {
                            order: this.orders.get(oid),
                            pollCount: 1
                        });
                    }
                }
                
                // Process REMOVED orders
                for (const oid of removed) {
                    if (this.pendingRemoves.has(oid)) {
                        // Increment poll count
                        const pollCount = this.pendingRemoves.get(oid) + 1;
                        this.pendingRemoves.set(oid, pollCount);
                        
                        if (pollCount >= this.DEBOUNCE_POLL_COUNT) {
                            // CONFIRMED removal - will be excluded from broadcast
                            this.pendingRemoves.delete(oid);
                        } else {
                            // NOT YET CONFIRMED - keep showing (add back to current)
                            currentOrderIds.add(oid);
                        }
                    } else {
                        // First time missing - mark as pending removal (keep showing for now)
                        this.pendingRemoves.set(oid, 1);
                        currentOrderIds.add(oid);
                    }
                }
                
                // Process UNCHANGED orders - reset any pending state
                for (const oid of unchanged) {
                    this.pendingAdds.delete(oid);
                    this.pendingRemoves.delete(oid);
                }
                
                // Build broadcast list: exclude unconfirmed adds, include pending removes still showing
                const idsToShow = new Set([...currentOrderIds].filter(oid => {
                    // Exclude orders still pending confirmation (in pendingAdds with count < 2)
                    if (this.pendingAdds.has(oid)) {
                        return false; // Don't show yet
                    }
                    return true; // Show confirmed orders and pending removes
                }));
                
                ordersArray = Array.from(idsToShow)
                    .map(oid => this.orders.get(oid))
                    .filter(order => order !== undefined);
                
                // Update previous state to what we ACTUALLY BROADCAST for next poll comparison
                this.previousOrderIds = new Set(idsToShow);
            }

            // Calculate data age
            const dataAge = this.lastDataUpdate ? Date.now() - this.lastDataUpdate : 0;
            const isStale = dataAge > 60000; // > 1 minute

            // Payload with unified timestamp - represents last data activity
            // Updated on polls, fills, cancels - any activity = fresh data
            const payload = {
                type: 'positions-update',
                positions: positionsArray,
                orders: ordersArray,
                accountSummary: this.accountSummary,
                timestamp: this.lastDataUpdate || Date.now(),
                connected: this.isConnected,
                dataAge: dataAge,           // Age in milliseconds
                isStale: isStale            // Boolean flag if > 1 minute old
            };

            // Cache payload for view restarts (5-second TTL)
            window.lastPositionsData = payload;
            
            // Emit CustomEvent instead of stdout/IPC
            window.dispatchEvent(new CustomEvent('positions-data', {
                detail: payload
            }));
            
            // Log warning if broadcasting stale data
            // if (isStale) {
            //     console.warn(`[POSITION-TRACKER] ⚠️ Broadcasting stale data (${Math.floor(dataAge / 1000)}s old)`);
            // }
            
            // LEADER ONLY: Broadcast to other tabs via BroadcastChannel
            if (this.mode === 'leader' && this.broadcastChannel) {
                this.broadcastChannel.postMessage({
                    type: 'POSITIONS_UPDATE',
                    tabId: window.tabId,
                    timestamp: this.lastDataUpdate || Date.now(),
                    data: {
                        positions: positionsArray,
                        orders: ordersArray,
                        accountSummary: this.accountSummary
                    }
                });
            }

        } catch (error) {
            // console.error('[POSITION-TRACKER] Error broadcasting data:', error);
        }
    }

    /**
     * Start in follower mode - listen to BroadcastChannel instead of WebSocket
     */
    async startFollowerMode() {
        console.log('[POSITION-TRACKER] Starting in FOLLOWER mode');
        
        if (!this.broadcastChannel) {
            this.broadcastChannel = new BroadcastChannel('atv4_positions');
            this.broadcastChannel.onmessage = (e) => this.handleBroadcast(e.data);
        }
        
        this.broadcastChannel.postMessage({
            type: 'REQUEST_SNAPSHOT',
            tabId: window.tabId,
            timestamp: Date.now()
        });
        
        this.isRunning = true;
        this.mode = 'follower';
        console.log('[POSITION-TRACKER] ✅ Follower mode active, waiting for data from leader');
    }

    /**
     * Handle broadcasts from leader tab
     */
    handleBroadcast(message) {
        if (message.tabId === window.tabId) return;
        
        if (message.type === 'POSITIONS_UPDATE') {
            this.positions.clear();
            if (message.data.positions) {
                message.data.positions.forEach(p => this.positions.set(p.coin, p));
            }
            
            this.orders.clear();
            if (message.data.orders) {
                message.data.orders.forEach(o => this.orders.set(o.oid, o));
            }
            
            this.accountSummary = message.data.accountSummary || {};
            this.lastDataUpdate = message.timestamp;
            
            window.lastPositionsData = {
                positions: message.data.positions,
                orders: message.data.orders,
                accountSummary: message.data.accountSummary,
                timestamp: message.timestamp,
                connected: true,
                dataAge: 0,
                isStale: false
            };
            
            this.processTPSLOrders();
            
            window.dispatchEvent(new CustomEvent('positions-data', {
                detail: window.lastPositionsData
            }));
        }
        
        if (message.type === 'REQUEST_SNAPSHOT' && this.mode === 'leader') {
            this.sendSnapshot();
        }
    }

    /**
     * Send snapshot to requesting follower (leader only)
     */
    sendSnapshot() {
        if (this.mode !== 'leader') return;
        
        const positionsArray = Array.from(this.positions.values());
        const ordersArray = Array.from(this.orders.values());
        
        this.broadcastChannel.postMessage({
            type: 'POSITIONS_UPDATE',
            tabId: window.tabId,
            timestamp: this.lastDataUpdate || Date.now(),
            data: {
                positions: positionsArray,
                orders: ordersArray,
                accountSummary: this.accountSummary
            }
        });
    }

    /**
     * Set mode (for TabCoordinator)
     */
    async setMode(newMode) {
        if (this.mode === newMode) return;
        
        console.log(`[POSITION-TRACKER] Switching mode: ${this.mode} -> ${newMode}`);
        
        if (newMode === 'leader') {
            if (this.broadcastChannel) {
                this.broadcastChannel.close();
                this.broadcastChannel = null;
            }
            await this.stop();
            await this.start(this.publicAddress, 'leader');
        } else if (newMode === 'follower') {
            await this.stop();
            await this.start(this.publicAddress, 'follower');
        }
    }

    /**
     * Public method for manual refresh (called by some views)
     * Triggers immediate poll for latest data
     */
    async poll() {
        await this.pollData();
    }

    /**
     * Poll only a specific DEX (for targeted refresh after chase modifications)
     * Efficient 2-API-call method for updating single DEX without affecting others
     * @param {string} dexName - DEX to poll ('main', 'xyz', 'flx', 'vntl')
     */
    async pollSpecificDex(dexName) {
        // Mutex: If already polling, wait for it to finish then execute
        if (this.isPolling) {
            await new Promise((resolve) => {
                this.pollQueue.push(resolve);
            });
            return this.pollSpecificDex(dexName);
        }
        
        this.isPolling = true;
        const pollStartTime = Date.now();
        
        try {
            // Convert 'main' to empty string for API call
            const dexParam = dexName === 'main' ? '' : dexName;
            // console.log(`[POSITION-TRACKER] 🎯 Polling specific DEX: ${dexName}`);
            
            // Query clearinghouse state for this DEX
            const state = await this.infoClient.clearinghouseState({ 
                user: this.publicAddress, 
                dex: dexParam 
            });
            
            // Query open orders for this DEX
            const orders = await this.infoClient.openOrders({ 
                user: this.publicAddress, 
                dex: dexParam 
            });
            
            // WS position updates are DISABLED - always process API data
            // Record fresh data timestamp
            this.lastDataUpdate = Date.now();
            this.isConnected = true;
            
            // MERGE logic - only update orders from this specific DEX
            // Remove old orders from THIS DEX only
            for (const [oid, order] of this.orders.entries()) {
                if (order.dex === dexName) {
                    this.orders.delete(oid);
                }
            }
            
            // Add fresh orders from this DEX
            if (orders && Array.isArray(orders)) {
                orders.forEach(order => {
                    const orderData = {
                        coin: order.coin,
                        side: order.side === 'A' ? 'SELL' : 'BUY',
                        size: parseFloat(order.sz),
                        limitPx: parseFloat(order.limitPx),
                        oid: order.oid,
                        isPositionTpsl: order.isPositionTpsl,
                        reduceOnly: order.reduceOnly || false,
                        cloid: order.cloid || null,
                        timestamp: order.timestamp || Date.now(),
                        dex: dexName,
                        isHip3: order.coin.includes(':')
                    };
                    this.orders.set(order.oid, orderData);
                });
            }
            
            // Update positions from this DEX (chase might have filled/modified position)
            // Remove old positions from THIS DEX only
            for (const [coin, pos] of this.positions.entries()) {
                if (pos.dex === dexName) {
                    this.positions.delete(coin);
                }
            }
            
            // Add fresh positions from this DEX
            if (state && state.assetPositions) {
                state.assetPositions.forEach(assetPos => {
                    const pos = assetPos.position;
                    if (pos && Math.abs(parseFloat(pos.szi)) > 0) {
                        const isHip3 = pos.coin.includes(':');
                        this.positions.set(pos.coin, {
                            coin: pos.coin,
                            size: parseFloat(pos.szi),
                            entryPrice: parseFloat(pos.entryPx || 0),
                            unrealizedPnl: parseFloat(pos.unrealizedPnl || 0),
                            side: parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT',
                            dex: dexName,
                            isHip3: isHip3
                        });
                    }
                });
            }
            
            // Process TP/SL orders
            this.processTPSLOrders();
            
            // Broadcast updated data
            this.broadcastData();
            
            // console.log(`[POSITION-TRACKER] ✅ pollSpecificDex(${dexName}): ${orders?.length || 0} orders refreshed`);
            
        } catch (error) {
            // console.error(`[POSITION-TRACKER] ❌ pollSpecificDex(${dexName}) failed:`, error);
        } finally {
            this.isPolling = false;
            
            // Resolve queued requests
            while (this.pollQueue.length > 0) {
                this.pollQueue.shift()();
            }
        }
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserPositionTracker = new BrowserPositionTracker();
    // console.log('✅ BrowserPositionTracker initialized');
}

export default BrowserPositionTracker;